import { Component, OnInit, ViewChild } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { ConfigServiceService } from 'src/app/config/shared/config-service.service';
import { WebComponentSettingsDto } from './shared/dto/web-component-settings.dto';
import { PublicLink } from './shared/dto/public-link.dto';
import { BetaFeatureToggleEnum } from 'src/app/config/shared/constants/beta-feature-toggle.enum';
import { EntityDomainService } from 'src/app/admin/entity-domains/shared/entity-domain.service';
import { ConfigService } from 'src/app/config/shared/config.service';
import { PublicLinksUpgradeDialogComponent } from './public-links-upgrade-dialog/public-links-upgrade-dialog.component';
import { PublicLinksProgressDialogComponent } from './public-links-progress-dialog/public-links-progress-dialog.component';
import { AuthService } from 'src/app/shared';

@Component({
  templateUrl: 'public-links.component.html',
  styleUrls: ['public-links.component.less'],
  selector: 'wl-public-links',
    standalone: false
})
export class PublicLinksComponent implements OnInit {
  @ViewChild(PublicLinksProgressDialogComponent) private _progressDialog: PublicLinksProgressDialogComponent;
  @ViewChild(PublicLinksUpgradeDialogComponent) private _upgradeDialog: PublicLinksUpgradeDialogComponent;

  webComponentSettings: WebComponentSettingsDto;
  publicLinks: Array<PublicLink>;

  busy = false;
  reallyDirty = false;
  loading = true;
  showUpgradeButton = false;
  hideTable = true;

  betaToggleEcom = false;
  betaToggleMemberPortal = false;

  domainSettingBatches: SelectItem[];
  showDomains = false;
  selectedSettingBatch: string;
  selectedDomain: string;
  legacyDomain: string;

  public constructor(
    private _configServiceService: ConfigServiceService,
    private _configService: ConfigService,
    private _entityDomainService: EntityDomainService,
    private _authService: AuthService
  ) { }
  ngOnInit(): void {
    this.populateDomains();
  }

  populateDomains() {
    this.hideTable = true;
    Promise.all([
      this._entityDomainService.getAllDomainSettingBatches(),
      this._authService.getUserInfo()
    ])
      .then(([domains, userInfo]) => {
        this.domainSettingBatches = domains.map((x) => {
          return { label: Object.keys(x)[0], value: Object.values(x)[0] }
        });
        this.showDomains = new Set(this.domainSettingBatches.map((x) => x.value)).size > 1;
        this.legacyDomain = userInfo.wli_tenant_https_url || '';
      })
      .finally(() => {
        this.getWebComponentSettings();
      });
  }

  getWebComponentSettings() {
    if (this.showDomains && this.selectedSettingBatch == null) {
      return;
    }

    this.hideTable = false;
    this.loading = true;

    this.selectedDomain = this.showDomains ?
      "https://" + this.domainSettingBatches.filter(x => x.value === this.selectedSettingBatch)[0].label :
      this.legacyDomain

    Promise.all([
      this._configServiceService.getWebComponentSettings(this.selectedSettingBatch, true),
      this._configService.hasBetaFeatureEnabled(BetaFeatureToggleEnum.Ecom),
      this._configService.hasBetaFeatureEnabled(BetaFeatureToggleEnum.MemberPortal)
    ])
      .then(([settings, ecomBeta, memberPortalBeta]) => {
        this.webComponentSettings = settings;
        this.betaToggleEcom = ecomBeta;
        this.betaToggleMemberPortal = memberPortalBeta;
        this.showUpgradeButton = !settings.RedirectLegacyToNew;
      })
      .finally(() => {
        this.populatePublicLinks();
      })
      .catch(() => {
        this.loading = false;
      });
  }

  populatePublicLinks() {
    this.publicLinks = new Array<PublicLink>();
    this.publicLinks.push({
      Name: "Directory Search Page",
      Latest: this.webComponentSettings.RedirectLegacyToNew,
      Url: this.buildLink("/search")
    } as PublicLink);

    this.publicLinks.push({
      Name: "Online Event Calendar",
      Latest: this.webComponentSettings.RedirectLegacyToNew,
      Url: this.buildLink("/events")
    } as PublicLink);

    this.publicLinks.push({
      Name: "eCommerce Online Store",
      Latest: this.webComponentSettings.RedirectLegacyToNew || this.betaToggleEcom,
      Url: this.buildLink("/store")
    } as PublicLink);

    this.publicLinks.push({
      Name: "Member Portal",
      Latest: this.webComponentSettings.RedirectLegacyToNew || this.betaToggleMemberPortal,
      Url: this.buildLink("/portal")
    } as PublicLink);

    this.publicLinks.push({
      Name: "News Articles",
      Latest: this.webComponentSettings.RedirectLegacyToNew,
      Url: this.buildLink("/news")
    } as PublicLink);

    this.publicLinks.push({
      Name: "Coupons",
      Latest: this.webComponentSettings.RedirectLegacyToNew,
      Url: this.buildLink("/coupons")
    } as PublicLink);

    this.publicLinks.push({
      Name: "Community Event Submission",
      Latest: this.webComponentSettings.RedirectLegacyToNew || this.betaToggleMemberPortal,
      Url: this.buildLink("/atlas/events/eventsubmission")
    } as PublicLink);

    this.loading = false;
  }

  buildLink(path: string) {
    return this.selectedDomain + path;
  }

  updateSettings(settings: WebComponentSettingsDto) {
    this.loading = true;
    this._configServiceService.upsertWebComponentSettings(settings, this.selectedSettingBatch)
      .then((res) => {
        this.webComponentSettings = settings;
        this.showUpgradeButton = !settings.RedirectLegacyToNew;
        this.populatePublicLinks();
        this._progressDialog.open();
      })
  }

  openUpgradeDialog() {
    this._upgradeDialog.open();
  }
}
